// @ts-nocheck
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import useIsMounted from '../../../vodea/utilities/hooks/useIsMounted';
import { Helmet } from 'react-helmet-async';
import VuiBreadcrumb from '../../../vodea/@vodea-ui/components/VuiBreadcrumb';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useState } from '@hookstate/core/dist';
import DeliveryOrderRepository from '../../../repositories/DeliveryOrderRepository';
import { AxiosError, AxiosResponse } from 'axios';
import { showToast } from '../../../vodea/utilities/helpers/global';
import * as _ from 'lodash';
import moment from 'moment';
import { $clone, defaultActionFormatter } from '../../../vodea/utilities';
import VuiActionModal from '../../../vodea/@vodea-ui/components/Modal/VuiActionModal';
import VuiDateRangePicker from '../../../vodea/@vodea-ui/components/Forms/VuiDateRangePicker';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores';
import useScroll from '../../../vodea/utilities/hooks/useScroll';
import DataTableInfiniteScroll, { ITableColumn } from '../../../vodea/@vodea-ui/components/DataTableInfiniteScroll';
import VuiSearch from '../../../vodea/@vodea-ui/components/VuiSearch';
import { DEFAULT_PAGE_SIZE } from '../../../constants';

const Delivery: React.FC<any> = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isMounted = useIsMounted();

  const tableDeleteId = useState(0);
  const showActionModal = useState(false);
  const permissions = useSelector((state: RootState) => state.permission.permission.permissions);
  const { isBottom } = useScroll();
  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const [isFetchingMoreData, setIsFetchingMoreData] = React.useState<boolean>(false);
  const [hasInitialize, setHasInitialize] = React.useState<boolean>(false);
  const [totalData, setTotalData] = React.useState<number>(0);

  const tableOnClickDelete = (id: number) => {
    tableDeleteId.set(id);
    showActionModal.set(true);
  };

  const tableHandleOnContinue = (e: React.MouseEvent) => {
    DeliveryOrderRepository.delete(tableDeleteId.get())
      .then((response: AxiosResponse) => {
        showToast(response.data.message, response.data.success ? 'success' : 'error');
        showActionModal.set(false);
        getTableData(true);
      })
      .catch((e: AxiosError) => {
        showToast(e.message, 'error');
      });
  };

  const tableConfig = useState({
    search: '',
    page: 1,
    per_page: DEFAULT_PAGE_SIZE,
    sorted_by: 'desc',
    order_by: 'date',
    total: 0,
    loading: false,

    // additional
    date_from: moment().format('YYYY-MM-DD'),
    date_to: moment().format('YYYY-MM-DD')
  });

  const tableData = useState([]);

  const tableColumns: ITableColumn[] = [
    {
      dataField: 'number',
      text: 'Number'
    },
    {
      dataField: 'driver_detail',
      text: 'Driver',
      render: (cell: any) => {
        return cell?.name;
      }
    },
    {
      dataField: 'date',
      text: 'Date',
      render: (cell: any) => {
        return moment(cell).format('D MMMM YYYY hh:mm A');
      }
    },
    {
      dataField: 'print_count',
      text: 'Print Count'
    },
    {
      dataField: 'action',
      text: 'Action',
      render: defaultActionFormatter,
      formatExtraData: {
        location: location.pathname,
        actionDelete: tableOnClickDelete,
        permission: 'delivery-order',
        permissions: permissions
      }
    }
  ];

  const changeTableLoading = (val: boolean) => {
    if (isMounted.current) {
      tableConfig.loading.set(val);
    }
  };

  const getTableData = async (reset: boolean = false, forNextPage: boolean = false) => {
    if (reset) tableConfig.page.set(1);
    if (isMounted.current && forNextPage) {
      setIsFetchingMoreData(true);
      tableConfig.page.set(tableConfig.page.get() + 1);
    } else {
      changeTableLoading(true);
    }

    if (isMounted.current) {
      setHasMore(true);
    }

    const conf = _.omit($clone(tableConfig.value), 'loading');

    let params: any = {};

    Object.keys(conf).forEach(prop => {
      if (conf[prop]) {
        params[prop] = conf[prop];
      }
    });

    if (isMounted.current && !forNextPage) tableData.set([]);

    await DeliveryOrderRepository.all({
      ...params,
      with: ['driverDetail']
    })
      .then((response: AxiosResponse) => {
        if (isMounted.current) {
          const { data: responseData } = response.data;
          tableData.merge(responseData);
          setIsFetchingMoreData(false);
          setHasInitialize(true);
          if (responseData.length == 0 || responseData.length < tableConfig.per_page.get()) {
            setHasMore(false);
          }
          if (reset) {
            setTotalData(response.data.total_data);
          }
        }

        changeTableLoading(false);
      })
      .catch((e: AxiosError) => {
        if (e.isAxiosError) showToast(e.message, 'error');
        if (isMounted.current) {
          setHasInitialize(true);
          setIsFetchingMoreData(false);
          setTotalData(0);
        }
        changeTableLoading(false);
      });
  };

  const handleTableDateRange = (start: any, end: any) => {
    tableConfig.date_from.set(start.format('YYYY-MM-DD'));
    tableConfig.date_to.set(end.format('YYYY-MM-DD'));

    getTableData(true);
  };

  useMemo(() => {
    getTableData(true);
  }, []);

  useMemo(() => {
    if (isBottom && hasInitialize && !isFetchingMoreData && hasMore) {
      getTableData(false, true);
    }
  }, [isBottom]);

  const handleTableSearch = (value: string) => {
    if (isMounted.current) {
      tableConfig.search.set(value);
      getTableData(true);
    }
  };

  const breadcrumbList = [
    {
      label: t('Delivery'),
      link: '/delivery'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('Delivery')}</title>
      </Helmet>

      <VuiBreadcrumb list={breadcrumbList} />

      <div className={'page-header-component'}>
        <h3 className={'title'}>{t('Delivery')}</h3>
        <div className={'btn-wrapper'}>
          <Link to={'/delivery/create'}>
            <button type={'button'} className={'btn btn-primary'}>
              <FontAwesomeIcon className={'icon icon-prefix'} icon={faPlus} />
              {t('Create Delivery')}
            </button>
          </Link>
        </div>
      </div>

      <div className={'card-paper'}>
        <div className={'card-content'}>
          <div className={'default-filter-section'}>
            <div className={'filter-item filter-special type-search'}>
              <VuiSearch value={tableConfig.search.get()} callback={handleTableSearch} />
            </div>

            <div className={'filter-item filter-special'}>
              <VuiDateRangePicker
                startDate={tableConfig.date_from.get()}
                endDate={tableConfig.date_to.get()}
                callback={handleTableDateRange}
                useRanges={true}
              />
            </div>
          </div>

          <DataTableInfiniteScroll
            columns={tableColumns}
            data={tableData.get()}
            propKey={'id'}
            isLoading={tableConfig.loading.get()}
            isFetchingMoreData={isFetchingMoreData}
            totalData={totalData}
          />
        </div>
      </div>

      <VuiActionModal show={showActionModal.get()} onHide={() => showActionModal.set(false)} onContinue={tableHandleOnContinue} />
    </>
  );
};

export default Delivery;
